import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { getIsPWAInstalled } from 'helpers/getIsPWAInstalled';

import {
  BetItemProvider,
  BetManager,
  constants,
  isEmptyCollection,
  isEventOffline,
  SiteConfigManager,
  useBetItem,
  useBetslipState,
  useGlobalBetslipState,
  useSettings,
  useTranslation,
} from '@atlas/core';
import { betslipNameParser } from '@atlas/display-layouts';
import { COMPONENT_TYPES, sportsbookRoutes } from '@atlas/routing';
import { BetOdds, Icon, Label } from '@atlas/ui';

import { emptySelectedBet } from 'common/Betslip/BetslipClassic';
import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { useNotificationsUIContext } from 'common/Popup/NotificationsUIContext';

const ExtendedFlyingBetslipMarketItem = ({ postfix = '' }) => {
  const { event, marketType } = useBetItem();

  return (
    <span className="betslip-extended-bet-label">
      {`${
        event.sportService === constants.SPORT_SERVICE.LIVE
          ? `${event?.info?.score?.homeScore} - ${event?.info?.score?.awayScore} `
          : ''
      }${marketType.name || '...'}${postfix}`}
    </span>
  );
};

const ExtendedFlyingBetslipCommonHeader = ({
  children,
  clearBetsHandler,
  isShowWarning,
  selectedBet,
}) => {
  return (
    <div className="betslip-extended-bet-title">
      <div className="betslip-extended-bet-type">
        {children}
        {selectedBet.betType !== constants.BET_TYPE.SYSTEM && !!selectedBet.odds && (
          <BetOdds className="odd-amount" odds={selectedBet.odds} />
        )}
        {isShowWarning && <Icon className="betslip-extended-warning" name="warning" />}
      </div>
      <button className="bet-order-outcome__close" onClick={clearBetsHandler}>
        <Icon height={16} name="trash-can" width={16} />
      </button>
    </div>
  );
};

const ExtendedFlyingBetslipParticipants = () => {
  const { event } = useBetItem();

  if (!event.name && !event.eventName) return '...';

  return (
    <span className="betslip-extended-bet-label">
      {event.outright
        ? event.name
        : `${event?.participants?.home?.fullName ?? ''} - ${event?.participants?.away?.fullName ?? ''}`}
    </span>
  );
};

const ExtendedFlyingBetslipSingleTitle = () => {
  const { event, marketType, outcome } = useBetItem();
  const translations = useTranslation();

  const outcomeNameFormat = useMemo(
    () => betslipNameParser(outcome, marketType, event, translations),
    [outcome, marketType, event, translations],
  );

  return <span className="bet-type-name">{outcomeNameFormat || '...'}</span>;
};

const ExtendedFlyingBetslip = () => {
  const { pathname } = useLocation();
  const { selectedBetTypeIndex, togglePopup } = useLocalBetslip();
  const { betItems, betTypes, errors, fullfited, loading } = useBetslipState();
  const { quickBet: isQuickBet } = useSettings();
  const {
    settings: { feeds },
  } = useGlobalBetslipState();
  const { isFlyingBetslip, setIsFlyingBetslip } = useNotificationsUIContext();
  const selectedBet = betTypes[selectedBetTypeIndex] || betItems[0] || emptySelectedBet;

  const singleBetPopup = SiteConfigManager.getConfig('config.config.betslip.single_bet_popup');
  const isRightLocation =
    (pathname.includes(sportsbookRoutes.favorites) ||
      pathname.includes(SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_LIVE)) ||
      pathname.includes(SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_PREMATCH))) &&
    !pathname.includes(SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO_LIVE));

  const isSystemOrMultipleType =
    selectedBet.betType === constants.BET_TYPE.MULTIPLE ||
    selectedBet.betType === constants.BET_TYPE.SYSTEM;

  const clearBetsHandler = React.useCallback(() => BetManager.clearBets(), []);

  const betslipExtendedPopupCls = React.useMemo(() => {
    const isPwaInstalled = getIsPWAInstalled();

    return classNames(
      'betslip-extended-popup',
      isQuickBet && 'betslip-extended-popup-with-quick-bet',
      isPwaInstalled && 'betslip-extended-popup-pwa',
      !isRightLocation && 'betslip-extended-popup-hidden',
    );
  }, [isQuickBet, isRightLocation]);

  const isShowWarning = React.useMemo(() => {
    if (!betItems.length || betItems.some(betConfig => !betConfig.event) || !fullfited || loading) {
      return false;
    }

    if (!isEmptyCollection(errors)) {
      return true;
    }

    return !!betItems.reduce((acc, { active, event, suspended } = {}) => {
      if (!acc && event) {
        const { id, onlySingle, source, sportId, sportService } = event;
        return (
          (!active && !suspended) ||
          sportService === constants.SPORT_SERVICE.ARCHIVED ||
          onlySingle ||
          !id ||
          isEventOffline({ feeds, source, sportId, sportService })
        );
      }
      return acc;
    }, false);
  }, [betItems, errors, feeds, fullfited, loading]);

  const systemOrMultipleComponent = useMemo(() => {
    if (!betItems.length || !isSystemOrMultipleType || isQuickBet) return null;
    let header;

    if (selectedBet.betType === constants.BET_TYPE.MULTIPLE) {
      header = (
        <>
          <Label className="bet-type-name" message={`betslip_button_${selectedBet.betType}`} />
          <span className="bet-type-count">({betItems.length})</span>
        </>
      );
    }

    if (selectedBet.betType === constants.BET_TYPE.SYSTEM) {
      header = (
        <>
          <Label
            className="bet-type-name"
            message="betslip_button_system"
            params={{
              outcomeAmount: selectedBet.outcomeAmount,
              winCombinationSize: selectedBet.winCombinationSize,
            }}
          />
          <span className="bet-type-count">
            {`${selectedBet.winCombinationSize}/${selectedBet.outcomeAmount}`}
          </span>
        </>
      );
    }

    const betItemsConfig = betItems.reduce(
      (acc, betItem) => {
        if (
          betItem.marketTypeId &&
          betItem.event?.marketTypes &&
          betItem.event.marketTypes[betItem.marketTypeId]?.name &&
          acc.visibleBetItems.length < 3
        ) {
          return {
            hasMore: acc.hasMore,
            visibleBetItems: [...acc.visibleBetItems, betItem],
          };
        } else {
          return {
            hasMore: acc.hasMore + 1,
            visibleBetItems: acc.visibleBetItems,
          };
        }
      },
      {
        hasMore: 0,
        visibleBetItems: [],
      },
    );

    if (!betItemsConfig.visibleBetItems.length) {
      return (
        <ExtendedFlyingBetslipCommonHeader
          clearBetsHandler={clearBetsHandler}
          isShowWarning={isShowWarning}
          selectedBet={selectedBet}
        >
          {header}
        </ExtendedFlyingBetslipCommonHeader>
      );
    }

    return (
      <div className="betslip-extended-main">
        <ExtendedFlyingBetslipCommonHeader
          clearBetsHandler={clearBetsHandler}
          isShowWarning={isShowWarning}
          selectedBet={selectedBet}
        >
          {header}
        </ExtendedFlyingBetslipCommonHeader>
        <div className="betslip-extended-bet-description">
          {betItemsConfig.visibleBetItems.map(betItem => (
            <BetItemProvider betItem={betItem} key={betItem.requestId}>
              <ExtendedFlyingBetslipMarketItem postfix="," />
            </BetItemProvider>
          ))}
          {!!betItemsConfig.hasMore && (
            <Label
              className="betslip-extended-bet-label betslip-extended-bet-label-italic"
              message="extended_floating_betslip_and_more"
              params={{
                outcomeNumber: betItemsConfig.hasMore,
              }}
            />
          )}
        </div>
      </div>
    );
  }, [betItems, isSystemOrMultipleType, selectedBet, clearBetsHandler, isShowWarning, isQuickBet]);

  useEffect(() => {
    setTimeout(
      () => setIsFlyingBetslip(betItems.length > 0),
      betItems.length === 1 && singleBetPopup ? 1000 : 0,
    );
  }, [betItems.length, singleBetPopup]);

  if (!isFlyingBetslip) return null;

  return (
    <div className={betslipExtendedPopupCls}>
      <div className="betslip-extended-top" onClick={togglePopup}>
        <div className="betslip-extended-title-wrp">
          <Label className="sub-title-text betslip-extended-title" message="betslip" />
          <span className="bet-items-count">{betItems.length}</span>
        </div>
        <Icon height={30} name="arrow-down" width={30} />
      </div>
      {systemOrMultipleComponent}
      {!isSystemOrMultipleType && betItems.length === 1 && !isQuickBet && (
        <BetItemProvider betItem={betItems[0]}>
          <div className="betslip-extended-main">
            <ExtendedFlyingBetslipCommonHeader
              clearBetsHandler={clearBetsHandler}
              isShowWarning={isShowWarning}
              selectedBet={selectedBet}
            >
              <ExtendedFlyingBetslipSingleTitle />
            </ExtendedFlyingBetslipCommonHeader>
            <div className="betslip-extended-bet-description betslip-extended-bet-description-ellipsis">
              <ExtendedFlyingBetslipMarketItem />
              <div className="betslip-extended-divider" />
              <ExtendedFlyingBetslipParticipants />
            </div>
          </div>
        </BetItemProvider>
      )}
    </div>
  );
};

export default ExtendedFlyingBetslip;
